import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { markSlideViewed, markQuizCompleted, getActivityProgress } from '../utils/progressTracker';

const ACTIVITY_ID = 'deap-activite7';

const DEAPActivite7Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const audioRef = useRef(null);

  // Load saved progress on mount
  useEffect(() => {
    const savedProgress = getActivityProgress(ACTIVITY_ID);
    if (savedProgress && savedProgress.lastViewedSlide > 0) {
      // Optionally resume from last position
      // setCurrentSlide(savedProgress.lastViewedSlide);
    }
  }, []);

  const slides = [
    // Slide 1 - Titre
    {
      type: 'title',
      title: "Activité 7",
      subtitle: "Accueil, accompagnement et formation des pairs, des personnes en formation et des autres professionnels",
      gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 50%, #B45309 100%)"
    },
    // Slide 2 - Sommaire interactif
    {
      type: 'sommaire',
      title: "Sommaire",
      sections: [
        { num: "1", title: "Cadre réglementaire", items: ["Loi du 10 juillet 2014", "Convention de stage", "Droits et obligations"], color: "#EF4444", slideTarget: 3 },
        { num: "2", title: "Enjeux et contextes", items: ["Objectifs de l'accueil", "Stagiaire, apprentie, professionnelle"], color: "#F59E0B", slideTarget: 14 },
        { num: "3", title: "Méthodologie d'accompagnement", items: ["Préparation", "Premier jour", "Progression pédagogique"], color: "#8B5CF6", slideTarget: 20 },
        { num: "4", title: "Posture professionnelle", items: ["Qualités mobilisées", "Finalité", "Messages clés"], color: "#10B981", slideTarget: 28 }
      ]
    },
    // Slide 3 - Section Réglementation
    {
      type: 'section',
      title: "CADRE RÉGLEMENTAIRE",
      subtitle: "Loi du 10 juillet 2014 sur l'encadrement des stages",
      gradient: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
    },
    // Slide 4 - Contexte législatif
    {
      type: 'content',
      title: "Loi du 10 juillet 2014 - Contexte",
      definition: "La loi n°2014-788 du 10 juillet 2014 vise à mieux encadrer les stages en milieu professionnel.",
      content: "Cette loi s'applique à tous les organismes d'accueil, y compris les établissements d'accueil du jeune enfant (EAJE).",
      extraInfo: [
        { term: "Protéger", desc: "Les stagiaires contre les abus" },
        { term: "Éviter", desc: "Les stages substitués à des emplois" },
        { term: "Clarifier", desc: "Les responsabilités des structures d'accueil" },
        { term: "Renforcer", desc: "La dimension pédagogique du stage" }
      ]
    },
    // Slide 5 - Finalité pédagogique
    {
      type: 'content',
      title: "Le stage a une finalité pédagogique",
      definition: "Un stage ne peut avoir pour objet l'exécution d'une tâche régulière correspondant à un poste de travail permanent.",
      warning: "En crèche, le stagiaire ne doit jamais être comptabilisé dans les effectifs réglementaires.",
      extraInfo: [
        { term: "Le stagiaire n'est pas", desc: "Un salarié" },
        { term: "Il ne peut pas", desc: "Remplacer un professionnel absent" },
        { term: "Il ne peut pas", desc: "Pallier un surcroît d'activité" }
      ]
    },
    // Slide 6 - Convention de stage
    {
      type: 'contentLargeImage',
      title: "La convention de stage est obligatoire",
      definition: "Aucun stage ne peut débuter sans convention signée.",
      extraInfo: [
        { term: "Signataires", desc: "Établissement de formation, organisme d'accueil, stagiaire, tuteur pédagogique" },
        { term: "Si mineur", desc: "Le représentant légal doit également signer" }
      ],
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/j8s0g2r6_image.png"
    },
    // Slide 7 - Contenu convention
    {
      type: 'list',
      title: "Contenu de la convention de stage",
      items: [
        { term: "Objectifs pédagogiques", desc: "Ce que le stagiaire doit apprendre" },
        { term: "Dates et durée", desc: "Période exacte du stage" },
        { term: "Nom du maître de stage", desc: "Référent dans la structure" },
        { term: "Modalités d'évaluation", desc: "Comment le stage sera évalué" },
        { term: "Conditions d'encadrement", desc: "Organisation de l'accompagnement" }
      ],
      alertColor: "#EF4444"
    },
    // Slide 8 - Durée et gratification
    {
      type: 'content',
      title: "Durée et gratification",
      extraInfo: [
        { term: "Durée maximale", desc: "6 mois par année d'enseignement dans une même structure" },
        { term: "Gratification obligatoire", desc: "Dès 2 mois consécutifs (soit 44 jours à 7h/jour)" },
        { term: "Montant", desc: "Fixé par la réglementation nationale, réévalué régulièrement" }
      ],
      warning: "La gratification n'est pas un salaire. Le stagiaire ne bénéficie pas d'un contrat de travail."
    },
    // Slide 9 - Maître de stage
    {
      type: 'list',
      title: "Le maître de stage",
      items: [
        { term: "Accueille et intègre", desc: "Le stagiaire dans la structure" },
        { term: "Assure l'accompagnement", desc: "Pédagogique tout au long du stage" },
        { term: "Veille au respect", desc: "Des objectifs de formation" },
        { term: "Garantit la sécurité", desc: "Du stagiaire et des enfants" }
      ],
      alertColor: "#F59E0B"
    },
    // Slide 10 - Temps de présence
    {
      type: 'content',
      title: "Temps de présence et conditions",
      extraInfo: [
        { term: "Durée", desc: "Ne peut excéder la durée légale du travail" },
        { term: "Repos", desc: "Doit respecter les temps de repos" },
        { term: "Heures sup", desc: "Pas d'heures supplémentaires possibles" },
        { term: "Droits", desc: "Accès aux locaux, sécurité, restaurant collectif" }
      ]
    },
    // Slide 11 - Vigilance crèche
    {
      type: 'list',
      title: "Points de vigilance en crèche",
      items: [
        { term: "Jamais seul", desc: "Ne jamais laisser un stagiaire seul responsable d'un groupe" },
        { term: "Pas de responsabilité exclusive", desc: "De soin" },
        { term: "Confidentialité", desc: "Respecter la confidentialité des informations (enfants, familles)" },
        { term: "Encadrement strict", desc: "De la participation aux soins" }
      ],
      alertColor: "#EF4444"
    },
    // Slide 12 - Apports de la loi
    {
      type: 'list',
      title: "Les apports de la loi pour les structures",
      items: [
        { term: "Sécurisation juridique", desc: "Cadre clair pour tous" },
        { term: "Clarification", desc: "Des responsabilités de chacun" },
        { term: "Reconnaissance", desc: "Du rôle du maître de stage" },
        { term: "Valorisation", desc: "De la dimension pédagogique" },
        { term: "Professionnalisation", desc: "Des structures accueillant des stagiaires" }
      ],
      alertColor: "#10B981"
    },
    // Slide 13 - Synthèse loi
    {
      type: 'keyMessage',
      title: "Synthèse - La loi rappelle que",
      messages: [
        "Le stagiaire est en formation, pas en remplacement",
        "L'encadrement est obligatoire",
        "La convention est indispensable",
        "La gratification peut être due (> 2 mois)",
        "La sécurité et la pédagogie priment"
      ],
      gradient: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
    },
    // Slide 14 - Section Enjeux
    {
      type: 'section',
      title: "ENJEUX ET CONTEXTES",
      subtitle: "Pourquoi et comment accompagner",
      gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
    },
    // Slide 15 - Introduction
    {
      type: 'contentLargeImage',
      title: "L'accueil et l'accompagnement",
      definition: "Accueillir et accompagner un pair, un stagiaire ou un nouveau professionnel constitue une compétence professionnelle essentielle.",
      content: "Cette mission participe à la qualité des pratiques, à la cohérence d'équipe et à la sécurité affective et physique des enfants accueillis.",
      image: "https://customer-assets.emergentagent.com/job_41162ecb-7c79-456b-9cca-4200297a1acd/artifacts/jltlt6jc_image.png"
    },
    // Slide 16 - Enjeux
    {
      type: 'list',
      title: "Les enjeux de l'accueil",
      items: [
        { term: "Continuité", desc: "Garantir la continuité des soins et de l'accompagnement" },
        { term: "Transmission", desc: "Transmettre les valeurs et le projet pédagogique" },
        { term: "Sécurité", desc: "Assurer la sécurité des enfants" },
        { term: "Intégration", desc: "Favoriser l'intégration dans l'équipe" },
        { term: "Professionnalisation", desc: "Soutenir la professionnalisation" }
      ],
      alertColor: "#F59E0B"
    },
    // Slide 17 - Contextes d'accompagnement
    {
      type: 'content',
      title: "Les différents contextes d'accompagnement",
      definition: "L'activité 7 exige de présenter au minimum un contexte d'accompagnement et un contexte de formation.",
      extraInfo: [
        { term: "Contexte de formation", desc: "Stagiaire, apprentie" },
        { term: "Contexte d'accompagnement", desc: "Nouvelle professionnelle, remplacement" }
      ],
      warning: "Ces situations mobilisent des postures et des méthodes différentes."
    },
    // Slide 18 - Stagiaire vs Apprentie vs Pro
    {
      type: 'threeColumns',
      title: "Stagiaire, Apprentie ou Professionnelle ?",
      columns: [
        {
          title: "Stagiaire",
          items: ["Durée limitée", "Convention de stage", "Objectifs pédagogiques", "Pas de responsabilité complète"],
          color: "#3B82F6"
        },
        {
          title: "Apprentie",
          items: ["Salariée en formation", "Contrat d'apprentissage", "Responsabilités croissantes", "Accompagnement continu"],
          color: "#8B5CF6"
        },
        {
          title: "Professionnelle",
          items: ["Compétences validées", "Diplômée", "Intégration institutionnelle", "Autonome"],
          color: "#10B981"
        }
      ]
    },
    // Slide 19 - Remplacements
    {
      type: 'content',
      title: "Remplacement long vs court",
      extraInfo: [
        { term: "Remplacement long", desc: "Présentation complète du projet, protocoles, spécificités des enfants" },
        { term: "Remplacement court", desc: "Transmission synthétique et priorisée (sécurité, allergies, PAI, organisation)" }
      ],
      warning: "La disponibilité de l'équipe est indispensable pour répondre rapidement aux questions."
    },
    // Slide 20 - Section Méthodologie
    {
      type: 'section',
      title: "MÉTHODOLOGIE D'ACCOMPAGNEMENT",
      subtitle: "Structurer l'accueil et la formation",
      gradient: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)"
    },
    // Slide 21 - Préparation
    {
      type: 'list',
      title: "Préparation en amont",
      items: [
        { term: "Informer l'équipe", desc: "De l'arrivée de la personne" },
        { term: "Planning prévisionnel", desc: "Organiser les temps d'apprentissage" },
        { term: "Documents institutionnels", desc: "Mettre à disposition (projet, protocoles)" },
        { term: "Identifier les priorités", desc: "Temps d'apprentissage essentiels" }
      ],
      alertColor: "#8B5CF6",
      warning: "Cette anticipation permet d'éviter un accueil improvisé."
    },
    // Slide 22 - Premier jour
    {
      type: 'steps',
      title: "L'accueil le premier jour",
      steps: [
        { num: "1", title: "Direction", items: ["Présentation à la direction", "Formalités administratives"], color: "#3B82F6" },
        { num: "2", title: "Équipe", items: ["Présentation à chaque membre", "Rôle de chacun"], color: "#8B5CF6" },
        { num: "3", title: "Locaux", items: ["Visite détaillée", "Repères spatiaux"], color: "#10B981" },
        { num: "4", title: "Fonctionnement", items: ["Organisation générale", "Informations progressives"], color: "#F59E0B" }
      ]
    },
    // Slide 23 - Progression pédagogique
    {
      type: 'steps',
      title: "Progression pédagogique",
      steps: [
        { num: "1", title: "Observer", items: ["Observation active", "Soins, transmissions, interactions"], color: "#3B82F6" },
        { num: "2", title: "Expliquer", items: ["Donner du sens", "Éviter la reproduction mécanique"], color: "#8B5CF6" },
        { num: "3", title: "Démontrer", items: ["Démonstration professionnelle", "Modélisation"], color: "#EC4899" },
        { num: "4", title: "Pratiquer", items: ["Mise en pratique accompagnée", "Supervision"], color: "#10B981" },
        { num: "5", title: "Analyser", items: ["Analyse réflexive", "Questions ouvertes"], color: "#F59E0B" }
      ]
    },
    // Slide 24 - Questions réflexives
    {
      type: 'list',
      title: "Questions pour l'analyse réflexive",
      items: [
        { term: "Qu'avez-vous observé ?", desc: "Décrire les faits" },
        { term: "Comment vous êtes-vous sentie ?", desc: "Exprimer les émotions" },
        { term: "Que pourriez-vous améliorer ?", desc: "Identifier les axes de progression" },
        { term: "Qu'avez-vous appris ?", desc: "Formaliser les apprentissages" }
      ],
      alertColor: "#8B5CF6"
    },
    // Slide 25 - Adaptation
    {
      type: 'content',
      title: "Adaptation aux caractéristiques",
      definition: "Chaque personne possède des caractéristiques différentes. L'accompagnement doit être individualisé.",
      extraInfo: [
        { term: "Certaines", desc: "Sont à l'aise rapidement" },
        { term: "D'autres", desc: "Sont timides ou anxieuses" },
        { term: "Certaines", desc: "Ont des difficultés techniques" },
        { term: "D'autres", desc: "Ont besoin d'être rassurées" }
      ]
    },
    // Slide 26 - Progression autonomie
    {
      type: 'steps',
      title: "Progression vers l'autonomie",
      steps: [
        { num: "1", title: "Tâches simples", items: ["Actions basiques", "Faible responsabilité"], color: "#3B82F6" },
        { num: "2", title: "Responsabilités partielles", items: ["Participation encadrée", "Validation progressive"], color: "#8B5CF6" },
        { num: "3", title: "Prise en charge encadrée", items: ["Responsabilité supervisée", "Feedback régulier"], color: "#10B981" },
        { num: "4", title: "Autonomie progressive", items: ["Confiance accordée", "Accompagnement distant"], color: "#F59E0B" }
      ]
    },
    // Slide 27 - Feedback
    {
      type: 'content',
      title: "Le feedback constructif",
      definition: "Donner un feedback constructif signifie valoriser et proposer des axes d'amélioration.",
      content: "Il est important de savoir valoriser les réussites et formuler des axes d'amélioration de manière constructive.",
      warning: "Ne pas critiquer les erreurs de manière négative. Éviter les comparaisons avec d'autres."
    },
    // Slide 28 - Section Posture
    {
      type: 'section',
      title: "POSTURE PROFESSIONNELLE",
      subtitle: "Qualités et finalités",
      gradient: "linear-gradient(135deg, #10B981 0%, #059669 100%)"
    },
    // Slide 29 - Qualités
    {
      type: 'list',
      title: "Les qualités professionnelles mobilisées",
      items: [
        { term: "Patience", desc: "Accepter le rythme de chacun" },
        { term: "Bienveillance", desc: "Attitude positive et encourageante" },
        { term: "Écoute", desc: "Capacité d'écoute active" },
        { term: "Clarté", desc: "Explications accessibles" },
        { term: "Disponibilité", desc: "Être présent pour répondre aux questions" },
        { term: "Adaptabilité", desc: "S'ajuster aux besoins" },
        { term: "Analyse", desc: "Capacité de prise de recul" },
        { term: "Éthique", desc: "Posture professionnelle exemplaire" }
      ],
      alertColor: "#10B981"
    },
    // Slide 30 - Finalité
    {
      type: 'content',
      title: "Finalité professionnelle",
      definition: "Accueillir et former un pair ou une personne en formation participe à la qualité de l'accueil des enfants et des familles.",
      extraInfo: [
        { term: "Cohérence d'équipe", desc: "Pratiques harmonisées" },
        { term: "Sécurité des pratiques", desc: "Protocoles respectés" },
        { term: "Transmission des valeurs", desc: "Professionnelles partagées" },
        { term: "Professionnalisation", desc: "Des futurs professionnels" }
      ]
    },
    // Slide 31 - Ce qu'évalue l'activité 7
    {
      type: 'content',
      title: "Ce que l'Activité 7 évalue",
      definition: "L'activité 7 met en évidence la capacité à transmettre, à accompagner et à analyser sa posture de formateur au sein d'une équipe pluridisciplinaire.",
      extraInfo: [
        { term: "Transmettre", desc: "Savoir expliquer et montrer" },
        { term: "Accompagner", desc: "Soutenir la progression" },
        { term: "Analyser", desc: "Prendre du recul sur sa pratique" }
      ]
    },
    // Slide 32 - Messages clés
    {
      type: 'keyMessage',
      title: "Messages clés à retenir",
      messages: [
        "Former et accompagner ne s'improvisent pas",
        "Un accueil structuré sécurise et limite les erreurs",
        "L'observation est la première étape pédagogique",
        "L'accompagnement doit être individualisé",
        "L'autonomie s'acquiert progressivement"
      ],
      gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
    },
    // Slide 33 - Félicitations
    {
      type: 'congratulations',
      title: "Félicitations !",
      message: "Vous avez terminé le cours sur l'Activité 7 : Accueil, accompagnement et formation.",
      nextStep: "Testez vos connaissances avec le quiz !",
      gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 50%, #B45309 100%)"
    }
  ];

  // Quiz questions - 20 questions
  const quizQuestions = [
    {
      id: 1,
      question: "Quel est l'objectif principal de l'accueil d'un stagiaire en structure ?",
      options: [
        "Lui confier immédiatement un groupe",
        "Garantir sa sécurité",
        "Favoriser sa professionnalisation",
        "Évaluer ses connaissances théoriques"
      ],
      correct: 2
    },
    {
      id: 2,
      question: "Quel document encadre obligatoirement la présence d'un stagiaire ?",
      options: [
        "Un contrat de travail",
        "Une convention de stage",
        "Un règlement intérieur",
        "Un protocole interne"
      ],
      correct: 1
    },
    {
      id: 3,
      question: "Pourquoi est-il important de préparer l'arrivée d'un stagiaire en amont ?",
      options: [
        "Pour gagner du temps",
        "Pour éviter un accueil improvisé",
        "Pour réduire la charge de travail",
        "Pour limiter les questions"
      ],
      correct: 1
    },
    {
      id: 4,
      question: "Quelle est la première étape pédagogique recommandée ?",
      options: [
        "L'évaluation",
        "L'autonomie complète",
        "L'observation",
        "La correction"
      ],
      correct: 2
    },
    {
      id: 5,
      question: "Pourquoi verbaliser ses pratiques professionnelles ?",
      options: [
        "Pour justifier son expérience",
        "Pour transmettre du sens aux actions",
        "Pour impressionner le stagiaire",
        "Pour gagner du temps"
      ],
      correct: 1
    },
    {
      id: 6,
      question: "L'accompagnement doit être :",
      options: [
        "Identique pour tous",
        "Rapide",
        "Individualisé",
        "Strict"
      ],
      correct: 2
    },
    {
      id: 7,
      question: "Quelle est la différence principale entre une stagiaire et une professionnelle diplômée ?",
      options: [
        "La durée de présence",
        "La responsabilité exercée",
        "L'âge",
        "Le niveau scolaire"
      ],
      correct: 1
    },
    {
      id: 8,
      question: "L'apprentie a quel statut ?",
      options: [
        "Bénévole",
        "Élève uniquement",
        "Salariée en formation",
        "Intervenante extérieure"
      ],
      correct: 2
    },
    {
      id: 9,
      question: "Lors d'un remplacement court, quelles informations doivent être prioritaires ?",
      options: [
        "L'histoire de la structure",
        "Les règles de sécurité",
        "Les projets à long terme",
        "Les statistiques annuelles"
      ],
      correct: 1
    },
    {
      id: 10,
      question: "La transmission progressive des informations permet :",
      options: [
        "De limiter l'implication",
        "D'éviter la surcharge cognitive",
        "De tester la personne",
        "D'accélérer l'intégration"
      ],
      correct: 1
    },
    {
      id: 11,
      question: "Donner un feedback constructif signifie :",
      options: [
        "Critiquer les erreurs",
        "Mettre en évidence les échecs",
        "Valoriser et proposer des axes d'amélioration",
        "Comparer avec d'autres professionnels"
      ],
      correct: 2
    },
    {
      id: 12,
      question: "L'autonomie d'un stagiaire doit être :",
      options: [
        "Immédiate",
        "Progressive",
        "Limitée",
        "Facultative"
      ],
      correct: 1
    },
    {
      id: 13,
      question: "Présenter la personne accueillie à l'équipe permet :",
      options: [
        "De formaliser son statut",
        "De créer du lien et faciliter l'intégration",
        "De vérifier son niveau",
        "De gagner du temps"
      ],
      correct: 1
    },
    {
      id: 14,
      question: "Quelle qualité est indispensable pour accompagner ?",
      options: [
        "Autorité",
        "Compétitivité",
        "Bienveillance",
        "Rapidité"
      ],
      correct: 2
    },
    {
      id: 15,
      question: "Pourquoi encourager l'analyse réflexive ?",
      options: [
        "Pour évaluer",
        "Pour développer la posture professionnelle",
        "Pour corriger rapidement",
        "Pour comparer les pratiques"
      ],
      correct: 1
    },
    {
      id: 16,
      question: "L'intégration d'une professionnelle diplômée porte principalement sur :",
      options: [
        "Les gestes techniques",
        "La culture institutionnelle",
        "Les examens",
        "Les évaluations écrites"
      ],
      correct: 1
    },
    {
      id: 17,
      question: "Une mauvaise intégration peut avoir un impact sur :",
      options: [
        "L'organisation des vacances",
        "La qualité d'accueil des enfants",
        "Le budget",
        "Les horaires administratifs"
      ],
      correct: 1
    },
    {
      id: 18,
      question: "Pourquoi prioriser les informations transmises ?",
      options: [
        "Pour simplifier",
        "Pour éviter les oublis",
        "Pour structurer l'apprentissage",
        "Pour limiter la formation"
      ],
      correct: 2
    },
    {
      id: 19,
      question: "L'accompagnement s'inscrit dans :",
      options: [
        "Une logique d'évaluation",
        "Une logique de sanction",
        "Une démarche de professionnalisation",
        "Une obligation administrative"
      ],
      correct: 2
    },
    {
      id: 20,
      question: "L'activité 7 évalue principalement :",
      options: [
        "Les compétences techniques",
        "La capacité à transmettre et analyser sa posture",
        "La rapidité d'exécution",
        "Les connaissances théoriques"
      ],
      correct: 1
    }
  ];

  const getSlideText = (slide) => {
    let text = '';
    if (slide.title) text += slide.title + '. ';
    if (slide.subtitle) text += slide.subtitle + '. ';
    if (slide.definition) text += slide.definition + ' ';
    if (slide.content) text += slide.content + ' ';
    if (slide.warning) text += 'Attention : ' + slide.warning + ' ';
    if (slide.message) text += slide.message + ' ';
    if (slide.nextStep) text += slide.nextStep + ' ';
    
    if (slide.items) {
      slide.items.forEach(item => {
        text += item.term + ': ' + item.desc + '. ';
      });
    }
    if (slide.extraInfo) {
      slide.extraInfo.forEach(info => {
        text += info.term + ': ' + info.desc + '. ';
      });
    }
    if (slide.messages) {
      slide.messages.forEach(msg => {
        text += msg + '. ';
      });
    }
    if (slide.sections) {
      slide.sections.forEach(section => {
        text += section.title + ': ' + section.items.join(', ') + '. ';
      });
    }
    if (slide.steps) {
      slide.steps.forEach(step => {
        text += step.title + ': ' + step.items.join(', ') + '. ';
      });
    }
    if (slide.columns) {
      slide.columns.forEach(col => {
        text += col.title + ': ' + col.items.join(', ') + '. ';
      });
    }
    
    return text;
  };

  const handleListen = async () => {
    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setIsPlaying(false);
      setIsLoading(false);
      return;
    }

    const slide = slides[currentSlide];
    let textToSpeak = getSlideText(slide);
    
    if (textToSpeak.length > 800) {
      textToSpeak = textToSpeak.substring(0, 800) + '...';
    }

    textToSpeak = textToSpeak
      .replace(/AP/g, 'auxiliaire de puériculture')
      .replace(/DEAP/g, 'diplôme d\'état d\'auxiliaire de puériculture')
      .replace(/EAJE/g, 'établissement d\'accueil du jeune enfant')
      .replace(/PAI/g, 'projet d\'accueil individualisé');

    if (!textToSpeak || textToSpeak.trim() === '') {
      toast.info("Pas de contenu audio pour cette slide");
      return;
    }

    try {
      setIsLoading(true);
      setIsPlaying(true);
      const API_URL = window.location.origin || '';
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: textToSpeak, speed: 1.1 })
      });

      if (!response.ok) throw new Error('TTS failed');

      const data = await response.json();
      const audioData = data.audio_base64 || data.audio;
      
      setIsLoading(false);
      
      if (audioData) {
        const audioBlob = new Blob(
          [Uint8Array.from(atob(audioData), c => c.charCodeAt(0))],
          { type: 'audio/mp3' }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        audioRef.current = new Audio(audioUrl);
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => {
          toast.error("Erreur de lecture audio");
          setIsPlaying(false);
        };
        audioRef.current.play().catch(err => {
          console.error('Audio play error:', err);
          toast.error("Impossible de lire l'audio");
          setIsPlaying(false);
        });
      } else {
        toast.error("Pas de données audio reçues");
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      toast.error("Erreur lors de la génération audio");
      setIsPlaying(false);
      setIsLoading(false);
    }
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      const newSlide = currentSlide + 1;
      setCurrentSlide(newSlide);
      // Track progress
      markSlideViewed(ACTIVITY_ID, newSlide, slides.length);
    } else {
      setShowQuiz(true);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    // Track progress
    markSlideViewed(ACTIVITY_ID, index, slides.length);
  };

  const handleQuizAnswer = (questionId, answerIndex) => {
    if (!quizSubmitted) {
      setQuizAnswers({ ...quizAnswers, [questionId]: answerIndex });
    }
  };

  const submitQuiz = () => {
    let score = 0;
    quizQuestions.forEach(q => {
      if (quizAnswers[q.id] === q.correct) score++;
    });
    setQuizScore(score);
    setQuizSubmitted(true);
    // Track quiz completion
    markQuizCompleted(ACTIVITY_ID, score, quizQuestions.length);
    toast.success(`Quiz terminé ! Score: ${score}/${quizQuestions.length}`);
  };

  const resetQuiz = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(0);
  };

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-title"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">{slide.title}</h1>
            <p className="text-xl md:text-2xl text-center max-w-3xl opacity-90">{slide.subtitle}</p>
          </div>
        );

      case 'sommaire':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white" data-testid="slide-sommaire">
            <h2 className="text-3xl font-bold mb-8 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {slide.sections.map((section, idx) => (
                <div 
                  key={idx}
                  onClick={() => section.slideTarget && goToSlide(section.slideTarget - 1)}
                  className="p-5 rounded-xl cursor-pointer transition-all hover:scale-105 hover:shadow-xl"
                  style={{ backgroundColor: section.color + '20', borderLeft: `4px solid ${section.color}` }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg"
                      style={{ backgroundColor: section.color }}
                    >
                      {section.num}
                    </span>
                    <h3 className="text-xl font-semibold">{section.title}</h3>
                  </div>
                  <ul className="space-y-1 text-sm text-gray-300">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span style={{ color: section.color }}>•</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'section':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-section"
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-4 text-center">{slide.title}</h2>
            {slide.subtitle && <p className="text-xl md:text-2xl opacity-90">{slide.subtitle}</p>}
          </div>
        );

      case 'content':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white overflow-y-auto" data-testid="slide-content">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-amber-400">{slide.title}</h2>
            <div className="space-y-4">
              {slide.definition && (
                <div className="p-4 bg-amber-500/20 rounded-xl border-l-4 border-amber-500">
                  <p className="text-lg font-medium">{slide.definition}</p>
                </div>
              )}
              {slide.content && <p className="text-gray-300 text-lg leading-relaxed">{slide.content}</p>}
              {slide.warning && (
                <div className="p-4 bg-red-500/20 rounded-xl border-l-4 border-red-500">
                  <p className="text-red-200 font-medium">{slide.warning}</p>
                </div>
              )}
              {slide.extraInfo && (
                <div className="space-y-3 mt-4">
                  {slide.extraInfo.map((info, idx) => (
                    <div key={idx} className="p-3 bg-slate-700/50 rounded-lg">
                      <span className="font-semibold text-amber-300">{info.term} : </span>
                      <span className="text-gray-300">{info.desc}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'contentLargeImage':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-6 rounded-2xl text-white overflow-y-auto" data-testid="slide-content-large">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-amber-400">{slide.title}</h2>
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="lg:w-1/2 space-y-3">
                {slide.definition && (
                  <div className="p-3 bg-amber-500/20 rounded-xl border-l-4 border-amber-500">
                    <p className="text-base font-medium">{slide.definition}</p>
                  </div>
                )}
                {slide.content && <p className="text-gray-300 text-base leading-relaxed">{slide.content}</p>}
                {slide.extraInfo && (
                  <div className="space-y-2">
                    {slide.extraInfo.map((info, idx) => (
                      <div key={idx} className="p-2 bg-slate-700/50 rounded-lg">
                        <span className="font-semibold text-amber-300">{info.term} : </span>
                        <span className="text-gray-300 text-sm">{info.desc}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {slide.image && (
                <div className="lg:w-1/2 flex items-center justify-center">
                  <img src={slide.image} alt="" className="rounded-xl shadow-2xl max-h-[380px] w-auto object-contain" />
                </div>
              )}
            </div>
          </div>
        );

      case 'list':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white overflow-y-auto" data-testid="slide-list">
            <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: slide.alertColor || '#F59E0B' }}>{slide.title}</h2>
            <div className="space-y-4">
              {slide.items.map((item, idx) => (
                <div 
                  key={idx} 
                  className="p-4 rounded-xl transition-all hover:scale-[1.02]"
                  style={{ backgroundColor: (slide.alertColor || '#F59E0B') + '15', borderLeft: `4px solid ${slide.alertColor || '#F59E0B'}` }}
                >
                  <span className="font-bold text-lg" style={{ color: slide.alertColor || '#F59E0B' }}>{item.term}</span>
                  <p className="text-gray-300 mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
            {slide.warning && (
              <div className="mt-6 p-4 bg-red-500/20 rounded-xl border border-red-500/50 text-center">
                <p className="text-red-300 font-bold">{slide.warning}</p>
              </div>
            )}
          </div>
        );

      case 'steps':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white overflow-y-auto" data-testid="slide-steps">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-amber-400">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {slide.steps.map((step, idx) => (
                <div 
                  key={idx}
                  className="p-5 rounded-xl"
                  style={{ backgroundColor: step.color + '20', borderTop: `4px solid ${step.color}` }}
                >
                  <div className="mb-4">
                    <span 
                      className="inline-block px-3 py-1 rounded-full text-white font-bold text-sm mb-2"
                      style={{ backgroundColor: step.color }}
                    >
                      {step.num}
                    </span>
                    <h3 className="font-bold text-lg">{step.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {step.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                        <span style={{ color: step.color }}>•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'threeColumns':
        return (
          <div className="h-full bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl text-white" data-testid="slide-three-columns">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-amber-400">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {slide.columns.map((col, idx) => (
                <div 
                  key={idx}
                  className="p-5 rounded-xl"
                  style={{ backgroundColor: col.color + '20', borderTop: `4px solid ${col.color}` }}
                >
                  <h3 className="font-bold text-xl mb-4" style={{ color: col.color }}>{col.title}</h3>
                  <ul className="space-y-2">
                    {col.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-300">
                        <CheckCircle className="w-4 h-4 flex-shrink-0 mt-1" style={{ color: col.color }} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'keyMessage':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-key-message"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8">{slide.title}</h2>
            <div className="space-y-4 max-w-2xl">
              {slide.messages.map((msg, idx) => (
                <div key={idx} className="flex items-center gap-4 bg-white/10 p-4 rounded-xl backdrop-blur-sm">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <p className="text-lg">{msg}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'congratulations':
        return (
          <div 
            className="h-full flex flex-col items-center justify-center text-white p-8 rounded-2xl"
            style={{ background: slide.gradient }}
            data-testid="slide-congratulations"
          >
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">{slide.title}</h2>
            <p className="text-xl text-center max-w-2xl mb-8 opacity-90">{slide.message}</p>
            <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
              <p className="text-lg font-medium">{slide.nextStep}</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (showQuiz) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-amber-900 to-slate-900 p-4 md:p-8" data-testid="quiz-container">
        <div className="max-w-4xl mx-auto">
          <div className="bg-slate-800/90 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-2xl">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">
              Quiz - Activité 7 : Accompagnement et formation
            </h2>
            
            {quizSubmitted && (
              <div className={`mb-6 p-4 rounded-xl text-center ${quizScore >= 14 ? 'bg-green-500/20 border border-green-500' : 'bg-amber-500/20 border border-amber-500'}`}>
                <p className="text-2xl font-bold text-white">
                  Score : {quizScore} / {quizQuestions.length}
                </p>
                <p className={`${quizScore >= 14 ? 'text-green-300' : 'text-amber-300'}`}>
                  {quizScore >= 14 ? 'Excellent travail !' : 'Continuez à réviser !'}
                </p>
              </div>
            )}

            <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
              {quizQuestions.map((q, qIdx) => (
                <div key={q.id} className="bg-slate-700/50 rounded-xl p-5">
                  <p className="text-white font-medium mb-4">
                    {qIdx + 1}. {q.question}
                  </p>
                  <div className="grid gap-2">
                    {q.options.map((option, oIdx) => {
                      let buttonClass = "w-full p-3 rounded-lg text-left transition-all ";
                      if (quizSubmitted) {
                        if (oIdx === q.correct) {
                          buttonClass += "bg-green-500/30 border-2 border-green-500 text-green-200";
                        } else if (quizAnswers[q.id] === oIdx && oIdx !== q.correct) {
                          buttonClass += "bg-red-500/30 border-2 border-red-500 text-red-200";
                        } else {
                          buttonClass += "bg-slate-600/50 text-gray-400";
                        }
                      } else {
                        buttonClass += quizAnswers[q.id] === oIdx 
                          ? "bg-amber-500/50 border-2 border-amber-500 text-white" 
                          : "bg-slate-600/50 hover:bg-slate-600 text-gray-300 border-2 border-transparent";
                      }
                      
                      return (
                        <button
                          key={oIdx}
                          onClick={() => handleQuizAnswer(q.id, oIdx)}
                          className={buttonClass}
                          disabled={quizSubmitted}
                          data-testid={`quiz-option-${q.id}-${oIdx}`}
                        >
                          <span className="flex items-center gap-3">
                            {quizSubmitted && oIdx === q.correct && <CheckCircle className="w-5 h-5 text-green-400" />}
                            {quizSubmitted && quizAnswers[q.id] === oIdx && oIdx !== q.correct && <XCircle className="w-5 h-5 text-red-400" />}
                            {option}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowQuiz(false)}
                className="flex-1 py-3 px-6 bg-slate-600 hover:bg-slate-500 text-white rounded-xl font-medium transition-all"
                data-testid="back-to-course-btn"
              >
                Retour au cours
              </button>
              {!quizSubmitted ? (
                <button
                  onClick={submitQuiz}
                  disabled={Object.keys(quizAnswers).length < quizQuestions.length}
                  className="flex-1 py-3 px-6 bg-amber-600 hover:bg-amber-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-all"
                  data-testid="submit-quiz-btn"
                >
                  Valider ({Object.keys(quizAnswers).length}/{quizQuestions.length})
                </button>
              ) : (
                <button
                  onClick={resetQuiz}
                  className="flex-1 py-3 px-6 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-medium transition-all"
                  data-testid="retry-quiz-btn"
                >
                  Recommencer
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-amber-900 to-slate-900 p-4 md:p-8" data-testid="presentation-container">
      <div className="max-w-6xl mx-auto">
        {/* Progress bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Progression</span>
            <span>{currentSlide + 1} / {slides.length}</span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-300"
              style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Slide content */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden min-h-[500px]">
          {renderSlide(slides[currentSlide])}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-all"
            data-testid="prev-slide-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            Précédent
          </button>

          <button
            onClick={handleListen}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl transition-all ${
              isPlaying 
                ? 'bg-red-600 hover:bg-red-500' 
                : 'bg-amber-600 hover:bg-amber-500'
            } text-white`}
            data-testid="listen-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Chargement...
              </>
            ) : isPlaying ? (
              <>
                <VolumeX className="w-5 h-5" />
                Arrêter
              </>
            ) : (
              <>
                <Volume2 className="w-5 h-5" />
                Écouter
              </>
            )}
          </button>

          <button
            onClick={nextSlide}
            className="flex items-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-500 text-white rounded-xl transition-all"
            data-testid="next-slide-btn"
          >
            {currentSlide === slides.length - 1 ? 'Quiz' : 'Suivant'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DEAPActivite7Presentation;
